/**
 * 解析 OSS 归档 UI 使用的 locale。
 * 优先级：入参 > URL lang > localStorage（locale / lang / i18nextLng）> 默认 zh-CN。
 * 不使用 require("i18next")：Vite ESM 下会拿到未初始化的另一份实例，导致始终落到 zh-CN。
 */

export const OSS_ARCHIVE_LOCALE_CODES = ["zh-CN", "en", "zh-TW", "es", "ko", "ja"] as const;

export type OssArchiveLocaleCode = (typeof OSS_ARCHIVE_LOCALE_CODES)[number];

const SUPPORTED = new Set<string>(OSS_ARCHIVE_LOCALE_CODES);

export function normalizeLocaleCode(raw: string): string {
	const lang = String(raw || "zh-CN").trim();
	const lower = lang.toLowerCase().replace(/_/g, "-");
	if (lower === "en" || lower.startsWith("en-")) {
		return "en";
	}
	if (lower === "zh-tw" || lower === "zh-hk" || lower === "zh-hant") {
		return "zh-TW";
	}
	if (lower === "es" || lower.startsWith("es-")) {
		return "es";
	}
	if (lower === "ko" || lower === "kr" || lower.startsWith("ko-") || lower.startsWith("kr-")) {
		return "ko";
	}
	if (lower === "ja" || lower === "jp" || lower.startsWith("ja-") || lower.startsWith("jp-")) {
		return "ja";
	}
	if (lower === "zh" || lower === "zh-cn" || lower === "zh-hans" || lower.startsWith("zh-")) {
		return "zh-CN";
	}
	return lang;
}

export function readLangFromHref(href?: string): string {
	const raw = href || (typeof window === "undefined" ? "" : window.location.href);
	if (!raw) {
		return "";
	}
	try {
		const url = new URL(raw, typeof window === "undefined" ? "http://localhost" : window.location.origin);
		const fromSearch = url.searchParams.get("lang") || url.searchParams.get("locale") || "";
		if (fromSearch) {
			return fromSearch;
		}
		const hash = url.hash || "";
		const qIndex = hash.indexOf("?");
		if (qIndex >= 0) {
			const params = new URLSearchParams(hash.slice(qIndex + 1));
			return params.get("lang") || params.get("locale") || "";
		}
	} catch {
		// ignore malformed href
	}
	return "";
}

function unwrapStoredValue(raw: string): string {
	const trimmed = String(raw || "").trim();
	if (!trimmed || trimmed === "undefined" || trimmed === "null") {
		return "";
	}
	try {
		const parsed = JSON.parse(trimmed);
		if (typeof parsed === "string") {
			return parsed;
		}
	} catch {
		// i18next-browser-languagedetector 写入纯文本
	}
	return trimmed;
}

export function readStoredOssArchiveLang(): string {
	if (typeof localStorage === "undefined") {
		return "";
	}
	for (const key of ["locale", "lang", "i18nextLng"]) {
		try {
			const value = unwrapStoredValue(localStorage.getItem(key) || "");
			if (value) {
				return value;
			}
		} catch {
			// ignore storage access errors
		}
	}
	return "";
}

export function resolveOssArchiveLocale(preferred?: string): string {
	const candidates = [preferred, readLangFromHref(), readStoredOssArchiveLang()];
	for (const item of candidates) {
		if (!item) {
			continue;
		}
		const normalized = normalizeLocaleCode(String(item));
		if (SUPPORTED.has(normalized)) {
			return normalized;
		}
	}
	return "zh-CN";
}

export function pickOssArchiveLocaleMessages<T extends Record<string, unknown>>(
	locales: Record<string, T> | undefined,
	locale: string,
	defaultLocale = "zh-CN"
): T | undefined {
	if (!locales) {
		return undefined;
	}
	const normalized = normalizeLocaleCode(locale);
	const direct = locales[locale] || locales[normalized];
	if (direct) {
		return direct;
	}
	const fallback = normalizeLocaleCode(defaultLocale);
	return locales[fallback] || locales[defaultLocale] || locales["zh-CN"];
}
